<template>
  <div class="cover-container w-100 mx-auto" id="main_page" style="overflow: hidden;">
    <!-- A-Frame AR 場景（沿用 ARpage / MangoGamePage / ChinaberryGamePage 的 8th Wall SLAM 對位方式）-->
    <a-scene v-if="isAFrameLoaded" ref="arScene" landing-page xrextras-loading xrextras-runtime-error
      renderer="colorManagement:true" xrweb="allowedDevices: any" embedded vr-mode-ui="enabled: false">

      <a-camera id="leafGameCamera" position="0 8 0" raycaster="objects: .cantap" cursor="fuse: false; rayOrigin: mouse;">
      </a-camera>

      <a-entity light="type: directional; intensity: 0.8; castShadow: false"
        xrextras-attach="target: camera; offset: 8 15 4" position="1 4.3 2.5">
      </a-entity>
      <a-light type="ambient" intensity="0.6"></a-light>

      <!-- 真實地面（不可見）：讓使用者直接點螢幕上看到的地面位置，用 A-Frame 內建 raycaster 求出真正的交點，
           比「鏡頭方向 × 固定距離」的土炮算法更貼合實際環境，對位後比較不會有飄移/懸空的感覺 -->
      <a-box id="leafGameGround" class="cantap" scale="1000 2 1000" position="0 -0.99 0"
        material="transparent: true; opacity: 0">
      </a-box>
    </a-scene>

    <!-- 對位提示：請使用者直接點選畫面上看到的落葉區域位置（不再是固定準心） -->
    <div class="place-hint" v-if="isShowPlaceHint && !isAnchorPlaced">
      <div class="place-hint-badge">🎯</div>
      <div class="place-hint-text">{{ languageTexts[currentLanguage].placeHint }}</div>
    </div>

    <!-- 遊戲 HUD -->
    <div class="game-hud" v-if="isGameActive">
      <div class="game-hud-row">{{ languageTexts[currentLanguage].progressLabel }}：{{ seedsFound }} / {{ winTarget }}</div>
    </div>

    <!-- 全螢幕點擊層：點選畫面上的落葉堆，讓落葉堆撥開（用 THREE.Raycaster 從實際觸碰點取交點，不依賴 A-Frame 內建 cursor） -->
    <div class="tap-capture-zone" v-if="isGameActive" @click="onSceneTap" @touchend.prevent="onSceneTap"></div>

    <!-- 過關畫面 -->
    <div class="finish-page" v-if="isShowWinPage">
      <div class="finish-content">
        <h1>{{ languageTexts[currentLanguage].winTitle }}</h1>
        <p class="finish-score">{{ languageTexts[currentLanguage].winSubtitle }}</p>
        <div class="btn_area">
          <div class="btn_css btn_css_primary" @click="goToBracelet">{{ languageTexts[currentLanguage].braceletButton }}</div>
          <div class="btn_css" @click="restartGame">{{ languageTexts[currentLanguage].restartButton }}</div>
          <div class="btn_css" @click="onBackToMap">{{ languageTexts[currentLanguage].backMapButton }}</div>
        </div>
      </div>
    </div>

    <!-- 開發者模式：調整落葉堆／無患子的大小與範圍 -->
    <div class="dev-toggle" v-if="devMode" @click="devPanelOpen = !devPanelOpen">⚙</div>
    <div class="dev-panel" v-if="devMode && devPanelOpen">
      <div class="dev-header">落葉堆／無患子大小調整</div>
      <div class="dev-label">落葉堆半徑（公尺）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devPileRadius', -1, 0.3)">−1</button>
        <button class="dev-btn" @click="devAdjust('devPileRadius', -0.2, 0.3)">−</button>
        <span class="dev-value">{{ devPileRadius.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjust('devPileRadius', 0.2)">+</button>
        <button class="dev-btn" @click="devAdjust('devPileRadius', 1)">+1</button>
      </div>
      <div class="dev-label">落葉碎片數量</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devLeafPieceCount', -50, 10)">−50</button>
        <button class="dev-btn" @click="devAdjust('devLeafPieceCount', -10, 10)">−</button>
        <span class="dev-value">{{ devLeafPieceCount }}</span>
        <button class="dev-btn" @click="devAdjust('devLeafPieceCount', 10)">+</button>
        <button class="dev-btn" @click="devAdjust('devLeafPieceCount', 50)">+50</button>
      </div>
      <div class="dev-label">落葉碎片大小（公尺）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devLeafSize', -0.02, 0.01)">−−</button>
        <button class="dev-btn" @click="devAdjust('devLeafSize', -0.005, 0.01)">−</button>
        <span class="dev-value">{{ devLeafSize.toFixed(3) }}</span>
        <button class="dev-btn" @click="devAdjust('devLeafSize', 0.005)">+</button>
        <button class="dev-btn" @click="devAdjust('devLeafSize', 0.02)">++</button>
      </div>
      <div class="dev-label">無患子大小（公尺）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devSeedSize', -0.02, 0.02)">−−</button>
        <button class="dev-btn" @click="devAdjust('devSeedSize', -0.005, 0.02)">−</button>
        <span class="dev-value">{{ devSeedSize.toFixed(3) }}</span>
        <button class="dev-btn" @click="devAdjust('devSeedSize', 0.005)">+</button>
        <button class="dev-btn" @click="devAdjust('devSeedSize', 0.02)">++</button>
      </div>
      <div class="dev-label">撥動作用半徑（公尺）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devStirRadius', -0.05, 0.05)">−−</button>
        <button class="dev-btn" @click="devAdjust('devStirRadius', -0.01, 0.05)">−</button>
        <span class="dev-value">{{ devStirRadius.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjust('devStirRadius', 0.01)">+</button>
        <button class="dev-btn" @click="devAdjust('devStirRadius', 0.05)">++</button>
      </div>
      <div class="dev-label">無患子最小間距（公尺）</div>
      <div class="dev-scale-row">
        <button class="dev-btn" @click="devAdjust('devMinSeedSeparation', -0.1, 0)">−−</button>
        <button class="dev-btn" @click="devAdjust('devMinSeedSeparation', -0.02, 0)">−</button>
        <span class="dev-value">{{ devMinSeedSeparation.toFixed(2) }}</span>
        <button class="dev-btn" @click="devAdjust('devMinSeedSeparation', 0.02)">+</button>
        <button class="dev-btn" @click="devAdjust('devMinSeedSeparation', 0.1)">++</button>
      </div>
      <div class="dev-row">
        <button class="dev-btn dev-save-btn" :disabled="!isAnchorPlaced" @click="regenerateLeafPile">依目前數值重新生成落葉堆</button>
      </div>
    </div>

    <TopNavigation :showIndexBtn="isShowIndexBtn" :showMapBtn="isShowMapBtn" :showDataBtn="false"
      :currentLanguage="currentLanguage" @back-to-index="onBackToIndex" @back-to-map="onBackToMap" />
  </div>
</template>

<script>
import Cookies from 'js-cookie'
import TopNavigation from '@/components/TopNavigation.vue'
import { withBase, getBasePath } from '@/utils/urlBase'

// 藏在落葉堆裡的無患子數量
const SEED_COUNT = 3
const LEAF_COLORS = ['#7C6A3D', '#8C7A4D', '#6B5B2A', '#9C8A5D']
// 每顆無患子旁邊額外加鋪幾片落葉，確保剛開始一定會被蓋住
const COVER_LEAVES_PER_SEED = 10

// 以下這些「大小」相關的預設值都可以在畫面上的開發者面板即時調整，這裡只是初始值
// 落葉堆整體半徑（公尺）
const DEFAULT_PILE_RADIUS = 5.2
// 組成落葉堆的碎片數量
const DEFAULT_LEAF_PIECE_COUNT = 150
// 單片落葉碎片的基準半徑（公尺），實際會再加上 0~0.04 的隨機值
const DEFAULT_LEAF_SIZE = 0.55
// 無患子的半徑（公尺）
const DEFAULT_SEED_SIZE = 0.3
// 每次點擊「撥動」落葉的作用半徑（公尺）：範圍內的落葉碎片會被撥到旁邊（不會消失），
// 讓範圍內原本被蓋住的無患子露出來
const DEFAULT_STIR_RADIUS = 1.5
// 無患子彼此的最小間距（公尺），避免生成位置太靠近
const DEFAULT_MIN_SEED_SEPARATION = 0.4

// 切換為 false 即關閉開發者調整面板
const DEV_MODE = true

export default {
  name: 'SoapberryGamePage',
  components: {
    TopNavigation,
  },
  data() {
    return {
      isShowIndexBtn: false,
      isShowMapBtn: false,
      isAFrameLoaded: false,
      isShowPlaceHint: false,
      isAnchorPlaced: false,
      isGameActive: false,
      isShowWinPage: false,
      seedsFound: 0,
      winTarget: SEED_COUNT,
      indexLink: window.location.protocol + '//' + window.location.host + getBasePath(),
      loadedScripts: [],
      // 開發者模式：即時調整落葉堆／無患子的大小與範圍
      devMode: false,
      devPanelOpen: false,
      devPileRadius: DEFAULT_PILE_RADIUS,
      devLeafPieceCount: DEFAULT_LEAF_PIECE_COUNT,
      devLeafSize: DEFAULT_LEAF_SIZE,
      devSeedSize: DEFAULT_SEED_SIZE,
      devStirRadius: DEFAULT_STIR_RADIUS,
      devMinSeedSeparation: DEFAULT_MIN_SEED_SEPARATION,
      languageTexts: {
        zh: {
          placeHint: '請直接點選畫面上無患子樹下的落葉區域',
          progressLabel: '已找到無患子',
          winTitle: '恭喜找到 3 顆無患子！',
          winSubtitle: '落葉下的無患子都被你找出來了',
          braceletButton: '戴上無患子手環',
          restartButton: '再玩一次',
          backMapButton: '返回地圖',
        },
        en: {
          placeHint: 'Tap directly on the fallen leaves under the Soapberry tree shown on screen',
          progressLabel: 'Soapberry Seeds Found',
          winTitle: 'You found all 3 soapberry seeds!',
          winSubtitle: 'All the seeds hidden under the leaves are found',
          braceletButton: 'Wear the Soapberry Bracelet',
          restartButton: 'Play Again',
          backMapButton: 'Back to Map',
        },
        bn: {
          placeHint: '画面に映っているムクロジの木の下の落ち葉を直接タップしてください',
          progressLabel: '見つけたムクロジの実',
          winTitle: 'ムクロジの実を3個見つけました！',
          winSubtitle: '落ち葉に隠れていた実を全部見つけました',
          braceletButton: 'ムクロジのブレスレットをつける',
          restartButton: 'もう一度遊ぶ',
          backMapButton: '地図に戻る',
        }
      }
    }
  },
  computed: {
    currentLanguage() {
      return Cookies.get('lang') || 'zh'
    },
  },
  mounted() {
    if (DEV_MODE) {
      this.devMode = true
    }

    // iOS Safari bfcache 修復：頁面從快取還原時強制重新載入，避免 XR session 衝突
    this._onPageShow = (event) => {
      if (event.persisted) {
        window.location.reload()
      }
    }
    window.addEventListener('pageshow', this._onPageShow)

    this._leafPieces = []
    this._seeds = []

    this.load8thWallScripts().then(() => {
      this.isAFrameLoaded = true
      this.$nextTick(() => {
        const scene = this.$refs.arScene
        if (!scene) return
        if (scene.hasLoaded) {
          this.onSceneLoaded()
        } else {
          scene.addEventListener('loaded', () => this.onSceneLoaded(), { once: true })
        }
      })
    }).catch(error => {
      console.error('Failed to load 8thWall scripts:', error)
      alert('AR 場景載入失敗，請重新整理頁面')
    })
  },
  beforeUnmount() {
    if (this._onPageShow) {
      window.removeEventListener('pageshow', this._onPageShow)
    }
    this.cleanupARScene()
  },
  methods: {
    onSceneLoaded() {
      this.removeVrModeUi()
      this.setupCameraReadyListener()
      this.setupGroundTapListener()
      setTimeout(() => {
        this.isShowIndexBtn = true
        this.isShowMapBtn = true
      }, 1000)
    },

    // 監聽地面點擊（沿用官方 aframe-world-effects-example 的 tap-place 寫法）：
    // camera 上的 raycaster + cursor 元件會在點擊 .cantap 物件時自動算出交點，
    // 從 event.detail.intersection.point 直接拿到真正對齊地面的世界座標
    setupGroundTapListener() {
      const ground = document.getElementById('leafGameGround')
      if (!ground) return
      this._onGroundTap = (event) => this.onGroundTap(event)
      ground.addEventListener('click', this._onGroundTap)
    },

    // 保險移除 A-Frame 自動插入的「進入 VR/AR」按鈕（vr-mode-ui="enabled:false" 未必能完全擋下）
    removeVrModeUi() {
      document.querySelectorAll('.a-enter-vr, .a-enter-ar, #vr-mode-ui-container').forEach(el => {
        if (el.parentNode) el.parentNode.removeChild(el)
      })
    },

    // 偵測相機取得權限並實際啟動後才顯示對位提示
    setupCameraReadyListener() {
      if (this._cameraModuleRegistered) return
      const vueInstance = this

      if (typeof XR !== 'undefined' && typeof XR.addCameraPipelineModule === 'function') {
        try {
          XR.addCameraPipelineModule({
            name: 'soapberry-game-camera-ready',
            onStart: () => {
              vueInstance.isShowPlaceHint = true
            }
          })
          this._cameraModuleRegistered = true
          return
        } catch (e) {
          console.warn('[XR] camera pipeline module 設定失敗，採用延遲 fallback：', e)
        }
      }
      setTimeout(() => {
        vueInstance.isShowPlaceHint = true
      }, 2000)
    },

    // 點擊地面時（僅第一次點擊有效），直接使用 A-Frame cursor 算好的交點作為落葉區域錨點，
    // 使用者現在是直接點選畫面上看到的落葉區域，而不是對準固定準心
    onGroundTap(event) {
      if (this.isAnchorPlaced) return
      const point = event.detail && event.detail.intersection ? event.detail.intersection.point : null
      if (!point) return
      this.placeAnchorAt(point)
    },

    // 建立一個不可見的錨點，標記落葉區域位置，作為落葉堆的生成參考點
    // 注意：空的 a-entity 本身沒有幾何/材質可渲染，不需另外設 visible:false
    // （若設為 false，會連同底下掛載的落葉堆子節點一起被隱藏）
    placeAnchorAt(point) {
      const scene = this.$refs.arScene
      if (!scene) return

      const anchor = document.createElement('a-entity')
      anchor.setAttribute('position', point)

      scene.appendChild(anchor)
      this._groundAnchorEl = anchor

      this.isAnchorPlaced = true
      this.isShowPlaceHint = false
      this.startGame()
    },

    startGame() {
      this.seedsFound = 0
      this.isShowWinPage = false
      this.isGameActive = true
      this.spawnLeafPile()
    },

    restartGame() {
      this.startGame()
    },

    // ── 開發者模式：調整落葉堆／無患子的大小與範圍 ──────────────────────
    devAdjust(field, delta, min = -Infinity) {
      this[field] = Math.max(min, parseFloat((this[field] + delta).toFixed(3)))
    },
    // 依目前面板數值重新生成落葉堆（重置本局進度，方便反覆調整測試）
    regenerateLeafPile() {
      if (!this._groundAnchorEl) return
      this.seedsFound = 0
      this.spawnLeafPile()
    },

    // ── 落葉堆生成：一整堆落葉由 devLeafPieceCount 個小碎片堆疊而成，
    // 另外埋 SEED_COUNT 顆無患子在堆裡（互相保持最小間距），
    // 再放一個看不見的圓形點擊面，用來取得使用者實際點到落葉堆的哪個位置。
    // 大小/範圍相關的數值都讀自 devXxx（開發者面板可即時調整）。
    spawnLeafPile() {
      this.clearLeafPile()
      if (!this._groundAnchorEl) return

      const pileRadius = this.devPileRadius

      const tapTarget = document.createElement('a-circle')
      tapTarget.setAttribute('radius', pileRadius + 0.15)
      tapTarget.setAttribute('rotation', '-90 0 0')
      tapTarget.setAttribute('position', '0 0.01 0')
      tapTarget.setAttribute('material', 'transparent: true; opacity: 0; side: double')
      this._groundAnchorEl.appendChild(tapTarget)
      this._tapTargetEl = tapTarget

      for (let i = 0; i < this.devLeafPieceCount; i++) {
        const angle = Math.random() * Math.PI * 2
        // 用 sqrt(random) 讓落葉在圓形範圍內大致均勻分佈，避免中心過度密集
        const radius = Math.sqrt(Math.random()) * pileRadius
        const x = Math.cos(angle) * radius
        const z = Math.sin(angle) * radius
        this.createLeafPieceAt(x, z)
      }

      const seedPositions = []
      for (let i = 0; i < SEED_COUNT; i++) {
        let sx, sz, attempts = 0
        do {
          const angle = Math.random() * Math.PI * 2
          const radius = Math.random() * (pileRadius * 0.7)
          sx = Math.cos(angle) * radius
          sz = Math.sin(angle) * radius
          attempts++
        } while (attempts < 20 && seedPositions.some(s => Math.hypot(s.x - sx, s.z - sz) < this.devMinSeedSeparation))
        seedPositions.push({ x: sx, z: sz })
      }

      // 無患子從一開始就是完整大小、真的擺在落葉堆裡（不是撥動後才變出來），
      // 靠周圍疊起來的落葉把它實際「蓋住」，撥開附近的落葉後自然就會露出來。
      // 每顆無患子旁邊額外多鋪一圈落葉，確保剛開始一定會被蓋住。
      this._seeds = seedPositions.map((pos, idx) => {
        for (let c = 0; c < COVER_LEAVES_PER_SEED; c++) {
          const angle = Math.random() * Math.PI * 2
          const dist = Math.random() * this.devStirRadius * 0.8
          this.createLeafPieceAt(pos.x + Math.cos(angle) * dist, pos.z + Math.sin(angle) * dist)
        }

        // 無患子（佔位圖形）：埋在落葉層附近的高度，才會被平躺的落葉實際擋住
        const seedEl = document.createElement('a-sphere')
        seedEl.setAttribute('radius', this.devSeedSize)
        seedEl.setAttribute('color', '#3E2A1A')
        seedEl.setAttribute('position', `${pos.x} 0.03 ${pos.z}`)
        this._groundAnchorEl.appendChild(seedEl)
        return { id: idx, el: seedEl, found: false }
      })
    },
    // 建立單片落葉並加進 _leafPieces（一般散落跟無患子周圍加鋪都共用這個）
    createLeafPieceAt(x, z) {
      // 高度變化縮小，讓落葉貼著地面、不會看起來浮空
      const y = 0.015 + Math.random() * 0.025

      // 用扁平的 a-plane 取代原本的三角錐，看起來才像一片片葉子而不是碎石
      const leaf = document.createElement('a-plane')
      const leafWidth = this.devLeafSize + Math.random() * 0.03
      const leafHeight = leafWidth * (1.3 + Math.random() * 0.5)
      leaf.setAttribute('width', leafWidth)
      leaf.setAttribute('height', leafHeight)
      const leafColor = LEAF_COLORS[Math.floor(Math.random() * LEAF_COLORS.length)]
      leaf.setAttribute('material', `color: ${leafColor}; side: double; shader: flat`)
      leaf.setAttribute('position', `${x} ${y} ${z}`)
      // 基準先轉 -90 度讓葉片平躺在地上（跟地面/點擊面同樣做法），
      // 再疊加小幅度傾斜＋隨機朝向，模擬自然散落的落葉，避免整片翻滾造成漂浮感
      const tiltX = (Math.random() - 0.5) * 20
      const tiltZ = (Math.random() - 0.5) * 20
      const headingY = Math.random() * 360
      leaf.setAttribute('rotation', `${-90 + tiltX} ${headingY} ${tiltZ}`)
      this._groundAnchorEl.appendChild(leaf)
      this._leafPieces.push({ el: leaf, cleared: false })
    },
    clearLeafPile() {
      if (this._tapTargetEl && this._tapTargetEl.parentNode) {
        this._tapTargetEl.parentNode.removeChild(this._tapTargetEl)
      }
      this._tapTargetEl = null

      if (this._leafPieces) {
        this._leafPieces.forEach(p => { if (p.el.parentNode) p.el.parentNode.removeChild(p.el) })
      }
      this._leafPieces = []

      if (this._seeds) {
        this._seeds.forEach(s => { if (s.el.parentNode) s.el.parentNode.removeChild(s.el) })
      }
      this._seeds = []
    },

    // 在指定世界座標「撥動」落葉：範圍內的落葉碎片被撥開消失。
    // 無患子不需要另外「彈出」──它本來就在場景裡，撥開蓋在上面的落葉後，
    // 鏡頭跟點擊 raycaster 自然就能看到／點到它（見 onSceneTap）。
    stirAt(point) {
      const THREE = AFRAME.THREE
      const vec = new THREE.Vector3()

      this._leafPieces.forEach(piece => {
        if (piece.cleared) return
        piece.el.object3D.getWorldPosition(vec)
        if (Math.hypot(vec.x - point.x, vec.z - point.z) <= this.devStirRadius) {
          this.clearLeafPiece(piece)
        }
      })
    },
    // 撥開單一落葉碎片：推到旁邊、留在原地，不會消失不見（只是不再擋路、不再參與撥動判定）
    clearLeafPiece(piece) {
      piece.cleared = true

      // 沿地面往外推開一段距離（不往上抬，避免看起來像飄起來），
      // 推開距離跟撥動半徑成比例，確保確實離開目前的撥動範圍
      const pos = piece.el.getAttribute('position')
      const angle = Math.random() * Math.PI * 2
      const pushDist = this.devStirRadius * (1.2 + Math.random() * 0.8)
      const kickX = Math.cos(angle) * pushDist
      const kickZ = Math.sin(angle) * pushDist
      piece.el.setAttribute('animation__kick', {
        property: 'position',
        to: `${pos.x + kickX} ${pos.y} ${pos.z + kickZ}`,
        dur: 280,
        easing: 'easeOutQuad'
      })
      // 順便加一點旋轉，看起來更像被撥動甩出去，而不是單純滑過去
      const rot = piece.el.getAttribute('rotation')
      piece.el.setAttribute('animation__spin', {
        property: 'rotation',
        to: `${rot.x} ${rot.y + (Math.random() - 0.5) * 180} ${rot.z}`,
        dur: 280,
        easing: 'easeOutQuad'
      })
    },
    // 點擊已經露出來（沒有被落葉擋住）的無患子本身：正式收集成功，計入進度，找滿即過關
    collectSeed(seed) {
      if (seed.found) return
      seed.found = true

      seed.el.setAttribute('animation__collect', {
        property: 'scale',
        to: '0 0 0',
        dur: 250,
        easing: 'easeInBack'
      })
      setTimeout(() => {
        if (seed.el.parentNode) seed.el.parentNode.removeChild(seed.el)
      }, 260)

      this.seedsFound += 1
      if (this.seedsFound >= this.winTarget) {
        setTimeout(() => this.winGame(), 500)
      }
    },
    winGame() {
      this.isGameActive = false
      this.isShowWinPage = true
    },

    // 點擊畫面任意處：對「無患子」「還沒撥開的落葉」「落葉堆點擊面」一起做 raycaster，取最近的交點來判斷。
    // 因為無患子跟落葉都是真的 3D 物件、有真實的前後遮蔽關係，
    // 只要無患子前面還有落葉擋著，射線一定會先打到落葉，點下去就只會撥動落葉；
    // 等旁邊的落葉被撥開、視線不再被擋住，射線才會直接打到無患子，這時點擊才算收集成功。
    onSceneTap(event) {
      if (!this.isGameActive || !this._tapTargetEl) return

      const scene = this.$refs.arScene
      const cameraEl = document.getElementById('leafGameCamera')
      if (!scene || !cameraEl) return

      const point = event.changedTouches ? event.changedTouches[0] : event
      if (!point) return

      const canvas = scene.canvas
      const canvasRect = canvas.getBoundingClientRect()
      const ndcX = ((point.clientX - canvasRect.left) / canvasRect.width) * 2 - 1
      const ndcY = ((point.clientY - canvasRect.top) / canvasRect.height) * -2 + 1

      const THREE = AFRAME.THREE
      const raycaster = new THREE.Raycaster()
      const cameraObj = cameraEl.getObject3D('camera')
      raycaster.setFromCamera({ x: ndcX, y: ndcY }, cameraObj)

      const meshTargets = []
      const meshInfo = new Map()

      this._seeds.forEach(seed => {
        if (seed.found) return
        const mesh = seed.el.getObject3D('mesh')
        if (mesh) {
          meshInfo.set(mesh, { type: 'seed', seed })
          meshTargets.push(mesh)
        }
      })

      this._leafPieces.forEach(piece => {
        if (piece.cleared) return
        const mesh = piece.el.getObject3D('mesh')
        if (mesh) {
          meshInfo.set(mesh, { type: 'leaf' })
          meshTargets.push(mesh)
        }
      })

      const groundMesh = this._tapTargetEl.getObject3D('mesh')
      if (groundMesh) {
        meshInfo.set(groundMesh, { type: 'ground' })
        meshTargets.push(groundMesh)
      }
      if (!meshTargets.length) return

      const intersects = raycaster.intersectObjects(meshTargets, true)
      if (!intersects.length) return

      let obj = intersects[0].object
      let hit = null
      while (obj) {
        if (meshInfo.has(obj)) {
          hit = meshInfo.get(obj)
          break
        }
        obj = obj.parent
      }
      if (!hit) return

      if (hit.type === 'seed') {
        this.collectSeed(hit.seed)
      } else {
        this.stirAt(intersects[0].point)
      }
    },

    // ── 場景清理 ────────────────────────────────────────────────────
    cleanupARScene() {
      this.clearLeafPile()

      if (this._groundAnchorEl && this._groundAnchorEl.parentNode) {
        this._groundAnchorEl.parentNode.removeChild(this._groundAnchorEl)
      }
      this._groundAnchorEl = null

      const ground = document.getElementById('leafGameGround')
      if (ground && this._onGroundTap) {
        ground.removeEventListener('click', this._onGroundTap)
      }

      const scene = this.$refs.arScene
      if (scene && scene.hasLoaded) {
        try { scene.pause() } catch (e) { /* ignore */ }
      }
      if (typeof XR !== 'undefined') {
        try { XR.stop() } catch (e) { /* ignore */ }
      }
    },

    onBackToIndex() {
      window.location.href = this.indexLink
    },
    onBackToMap() {
      this.cleanupARScene()
      window.location.href = './mapPage'
    },
    goToBracelet() {
      this.cleanupARScene()
      window.location.href = this.indexLink + 'soapberrybracelet'
    },

    // 載入 8thWall 所需的 scripts（與 ARpage.vue / MangoGamePage.vue / ChinaberryGamePage.vue 共用同一組本地檔案）
    load8thWallScripts() {
      return new Promise((resolve, reject) => {
        if (typeof window.AFRAME !== 'undefined') {
          resolve()
          return
        }

        const existingScripts = document.querySelectorAll('script[src*="8thwall"], script[src*="aframe"]')
        if (existingScripts.length > 0) {
          const checkAFrame = setInterval(() => {
            if (typeof window.AFRAME !== 'undefined') {
              clearInterval(checkAFrame)
              resolve()
            }
          }, 100)
          return
        }

        const aframeScript = document.createElement('script')
        aframeScript.src = withBase('/js/external/scripts/8frame-1.5.0.min.js')
        this.loadedScripts.push(aframeScript)

        aframeScript.onload = () => {
          const aframeExtraScript = document.createElement('script')
          aframeExtraScript.src = withBase('/js/external/xrextras/aframe-extras-6.1.1.min.js')
          this.loadedScripts.push(aframeExtraScript)

          aframeExtraScript.onload = () => {
            const xrExtrasScript = document.createElement('script')
            xrExtrasScript.src = withBase('/js/external/xrextras/xrextras.js')
            this.loadedScripts.push(xrExtrasScript)

            xrExtrasScript.onload = () => {
              const landingPageScript = document.createElement('script')
              landingPageScript.src = withBase('/js/external/landing-page/landing-page.js')
              this.loadedScripts.push(landingPageScript)

              landingPageScript.onload = () => {
                const eighthWallScript = document.createElement('script')
                eighthWallScript.src = withBase('/js/external/xr/xr.js')
                eighthWallScript.crossOrigin = 'anonymous'
                eighthWallScript.async = true
                eighthWallScript.setAttribute('data-preload-chunks', 'slam')
                this.loadedScripts.push(eighthWallScript)

                eighthWallScript.onload = () => resolve()
                eighthWallScript.onerror = () => reject(new Error('Failed to load 8thWall Web'))
                document.head.appendChild(eighthWallScript)
              }

              landingPageScript.onerror = () => reject(new Error('Failed to load Landing Page'))
              document.head.appendChild(landingPageScript)
            }

            xrExtrasScript.onerror = () => reject(new Error('Failed to load XR Extras'))
            document.head.appendChild(xrExtrasScript)
          }
          aframeExtraScript.onerror = () => reject(new Error('Failed to load XR Extras'))
          document.head.appendChild(aframeExtraScript)
        }

        aframeScript.onerror = () => reject(new Error('Failed to load A-Frame'))
        document.head.appendChild(aframeScript)
      })
    },
  }
}
</script>

<style scoped>
#main_page {
  height: 100svh;
  overflow: hidden;
  position: relative;
}

.place-hint {
  position: absolute;
  bottom: 32%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 500;
  pointer-events: none;
  text-align: center;
  color: #fff;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.6);
}

.place-hint-badge {
  font-size: 48px;
  animation: bob 1.2s ease-in-out infinite;
  display: inline-block;
}

@keyframes bob {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-10px);
  }
}

.place-hint-text {
  margin-top: 8px;
  font-size: 15px;
  max-width: 80vw;
  margin-left: auto;
  margin-right: auto;
}

.game-hud {
  position: fixed;
  top: 90px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 500;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(6px);
  border-radius: 16px;
  padding: 8px 20px;
  color: #fff;
  text-align: center;
}

.game-hud-row {
  font-size: 15px;
  font-family: 'MyFont', sans-serif;
}

.tap-capture-zone {
  position: fixed;
  inset: 0;
  z-index: 350;
  background: transparent;
}

.finish-page {
  position: fixed;
  inset: 0;
  z-index: 900;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.6);
}

.finish-content {
  background: #fff;
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  width: 80%;
  max-width: 360px;
}

.finish-content h1 {
  font-size: 22px;
  margin-bottom: 8px;
  color: #1a1a1a;
}

.finish-score {
  font-size: 16px;
  color: #477A17;
  margin-bottom: 20px;
}

.btn_area {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.btn_css {
  background: linear-gradient(180deg, #668C42 0%, #477A17 100%);
  color: #fff;
  border-radius: 999px;
  padding: 12px;
  font-size: 15px;
  font-family: 'MyFont', sans-serif;
  letter-spacing: 1px;
  cursor: pointer;
}

.btn_css_primary {
  background: linear-gradient(180deg, #D9A441 0%, #B4790F 100%);
}

/* ── 開發者模式 ── */
.dev-toggle {
  position: fixed;
  top: 80px;
  left: 10px;
  z-index: 10000;
  width: 36px;
  height: 36px;
  background: rgba(0, 0, 0, 0.55);
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  cursor: pointer;
  user-select: none;
}

.dev-panel {
  position: fixed;
  top: 120px;
  left: 10px;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.78);
  color: white;
  border-radius: 12px;
  padding: 12px;
  min-width: 230px;
  font-size: 13px;
  max-height: 60vh;
  overflow-y: auto;
}

.dev-header {
  font-weight: bold;
  font-size: 11px;
  color: #aaa;
  margin-bottom: 10px;
  letter-spacing: 0.5px;
}

.dev-label {
  font-size: 10px;
  color: #888;
  margin-bottom: 3px;
}

.dev-scale-row {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: 10px;
}

.dev-value {
  flex: 1;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
  font-variant-numeric: tabular-nums;
}

.dev-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 14px;
  cursor: pointer;
  touch-action: manipulation;
  line-height: 1;
}

.dev-btn:active {
  background: rgba(255, 255, 255, 0.3);
}

.dev-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dev-row {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
}

.dev-save-btn {
  background: rgba(67, 106, 178, 0.7);
  flex: 1;
}
</style>

<!-- 非 scoped：A-Frame 的進入 VR/AR 按鈕是由其自身 JS 動態插入 DOM，不帶 Vue 的 scoped 屬性，
     scoped style 選不到，所以另外用全域樣式強制隱藏（雙重保險，JS 端也會嘗試移除該節點）-->
<style>
.a-enter-vr,
.a-enter-ar,
#vr-mode-ui-container {
  display: none !important;
}
</style>
